import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Alert,
  Snackbar,
  LinearProgress,
  Tabs,
  Tab,
  InputAdornment,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SendIcon from '@mui/icons-material/Send';
import SmsIcon from '@mui/icons-material/Sms';
import RouterIcon from '@mui/icons-material/Router';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from '../settings/components/SettingsMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useAdministrator } from '../common/util/permissions';
import { useEffectAsync } from '../reactHelper';
import { useSelector } from 'react-redux';
import ApnConfigTab from './tabs/ApnConfigTab';
import DeviceConfigTab from './tabs/DeviceConfigTab';
import CommandHistoryTab from './tabs/CommandHistoryTab';
import CommandsTabV2 from './tabs/CommandsTabV2';
import SimCardIcon from '@mui/icons-material/SimCard';
import DevicesIcon from '@mui/icons-material/Devices';

// SMS API - usando proxy para evitar CORS
const SMS_API_URL = '/sms-api';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  fullHeight: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  },
  tabContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
    overflow: 'auto',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  templateCard: {
    height: '100%',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: theme.shadows[6],
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(33, 150, 243, 0.15)'
        : 'rgba(25, 118, 210, 0.08)',
      borderColor: theme.palette.primary.main,
    },
  },
  templateCardSelected: {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(33, 150, 243, 0.25)'
      : 'rgba(25, 118, 210, 0.12)',
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    borderStyle: 'solid',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(33, 150, 243, 0.30)'
        : 'rgba(25, 118, 210, 0.18)',
    },
  },
  templateName: {
    fontWeight: 600,
  },
  templateCommand: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    wordBreak: 'break-all',
  },
  previewBox: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    wordBreak: 'break-all',
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
  },
  manufacturerChip: {
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  statsValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  statusConnected: {
    color: theme.palette.success.main,
  },
  statusDisconnected: {
    color: theme.palette.error.main,
  },
}));

// ============================================================================
// CONSTANTES
// ============================================================================

// Templates de mensagens SMS para envio em massa
const SMS_MESSAGE_TEMPLATES = [
  { name: 'Alerta de Manutenção', message: 'ATENCAO: Seu veículo {placa} está com manutenção programada. Por favor, agende uma revisão.' },
  { name: 'Vencimento Documento', message: 'AVISO: O documento do veículo {placa} vence em breve. Regularize para evitar multas.' },
  { name: 'Excesso de Velocidade', message: 'ALERTA: Foi detectado excesso de velocidade no veículo {placa}. Dirija com segurança!' },
  { name: 'Jornada de Trabalho', message: 'ATENCAO: Motorista {motorista}, sua jornada diária está próxima do limite permitido.' },
  { name: 'Comunicado Geral', message: 'COMUNICADO: {mensagem}' },
  { name: 'Lembrete de Abastecimento', message: 'LEMBRETE: Veículo {placa} com nível de combustível baixo. Abasteça em breve.' },
];

const TAB_IDS = {
  COMMANDS: 0,
  APN_CONFIG: 1,
  DEVICE_CONFIG: 2,
  COMMAND_HISTORY: 3,
  SMS_CONFIG: 4,
  SMS_SEND: 5,
};

// ============================================================================
// TAB PANEL COMPONENT
// ============================================================================

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`configurator-tabpanel-${index}`}
    aria-labelledby={`configurator-tab-${index}`}
    style={{ height: '100%' }}
    {...other}
  >
    {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
  </div>
);


// ============================================================================
// SMS CONFIG TAB (Unified: Config + Balance)
// ============================================================================

const SmsConfigTab = ({
  classes, t, smsConfig, setSmsConfig, onSave, onTest, loading, testResult, balance, onRefreshBalance,
}) => (
  <Grid container spacing={3}>
    {/* Balance Card */}
    <Grid size={{ xs: 12, md: 3 }}>
      <Card className={classes.statsCard} sx={{ height: '100%' }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography className={classes.statsValue}>
            {balance !== null ? balance.toLocaleString() : '--'}
          </Typography>
          <Typography className={classes.statsLabel}>
            {t('smsCredits')}
          </Typography>
          {balance !== null && balance < 100 && (
            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
              {t('smsLowBalanceWarning')}
            </Alert>
          )}
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={onRefreshBalance}
            disabled={loading || !smsConfig.smsApiKey}
            sx={{ mt: 2 }}
          >
            {t('sharedRefresh')}
          </Button>
        </CardContent>
      </Card>
    </Grid>

    {/* Configuration Form */}
    <Grid size={{ xs: 12, md: 5 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('smsConfiguration')}
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          {t('smsComteleInfo')}
        </Alert>

        <TextField
          fullWidth
          margin="normal"
          label={t('smsApiKey')}
          type="password"
          placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
          value={smsConfig.smsApiKey || ''}
          onChange={(e) => setSmsConfig({ ...smsConfig, smsApiKey: e.target.value })}
          helperText={t('smsApiKeyHelper')}
        />

        <TextField
          fullWidth
          margin="normal"
          label={t('smsSenderId')}
          placeholder="TrackerSMS"
          value={smsConfig.smsSenderId || ''}
          onChange={(e) => setSmsConfig({ ...smsConfig, smsSenderId: e.target.value })}
          helperText={t('smsSenderIdHelper')}
        />

        {/* Server Configuration Section */}
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
          <RouterIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
          {t('serverConfiguration')}
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          {t('serverConfigHelper')}
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('serverDns')}
              placeholder="rastreador.exemplo.com.br"
              value={smsConfig.serverDns || ''}
              onChange={(e) => setSmsConfig({ ...smsConfig, serverDns: e.target.value })}
              helperText={t('serverDnsHelper')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('serverIp')}
              placeholder="123.45.67.89"
              value={smsConfig.serverIp || ''}
              onChange={(e) => setSmsConfig({ ...smsConfig, serverIp: e.target.value })}
              helperText={t('serverIpHelper')}
            />
          </Grid>
        </Grid>

        <Box mt={3} display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={onSave}
            disabled={loading || !smsConfig.smsApiKey}
          >
            {t('sharedSave')}
          </Button>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={onTest}
            disabled={loading || !smsConfig.smsApiKey}
          >
            {t('smsTestConnection')}
          </Button>
        </Box>
      </Paper>
    </Grid>

    {/* Status Panel */}
    <Grid size={{ xs: 12, md: 4 }}>
      <Paper sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" gutterBottom>
          {t('smsConnectionStatus')}
        </Typography>

        <Box py={2}>
          {testResult === null ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {t('smsStatusUnknown')}
              </Typography>
            </Box>
          ) : testResult.success ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircleIcon className={classes.statusConnected} />
              <Typography variant="body2" className={classes.statusConnected}>
                {t('smsStatusConnected')}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <ErrorIcon className={classes.statusDisconnected} />
              <Typography variant="body2" className={classes.statusDisconnected}>
                {t('smsStatusError')}: {testResult.error}
              </Typography>
            </Box>
          )}
        </Box>

        {testResult?.balance !== undefined && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('smsBalance')}: <strong>{testResult.balance}</strong> {t('smsCredits')}
          </Alert>
        )}

        <Box mt={3} p={2} bgcolor="action.hover" borderRadius={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            {t('smsApiInfo')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>{t('smsEndpoints')}:</strong><br />
            • POST /send - {t('smsSendMessage')}<br />
            • GET /credits - {t('smsCheckBalance')}<br />
            • GET /detailedreporting - {t('smsDeliveryReport')}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  </Grid>
);

// ============================================================================
// SMS SEND TAB
// ============================================================================

const SmsSendTab = ({ t, smsConfig, onSend, loading }) => {
  const [bulkMode, setBulkMode] = useState(false);
  const [phone, setPhone] = useState('');
  const [bulkPhones, setBulkPhones] = useState('');
  const [message, setMessage] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [result, setResult] = useState(null);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, errors: [] });

  const charCount = message.length;
  const smsCount = Math.ceil(charCount / 160) || 1;

  const phoneList = useMemo(() => {
    if (!bulkMode) return phone ? [phone] : [];
    return bulkPhones
      .split('\n')
      .map((p) => p.trim().replace(/\D/g, ''))
      .filter((p) => p.length >= 10);
  }, [bulkMode, phone, bulkPhones]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setMessage(template.message);
  };

  const handleSend = async () => {
    setResult(null);
    setSendProgress({ sent: 0, total: phoneList.length, errors: [] });

    if (phoneList.length === 1) {
      const res = await onSend(phoneList[0], message);
      setResult(res);
      if (res.success) {
        setPhone('');
        setBulkPhones('');
        setMessage('');
        setSelectedTemplate(null);
      }
    } else {
      // Envio em massa
      const errors = [];
      for (let i = 0; i < phoneList.length; i += 1) {
        const res = await onSend(phoneList[i], message);
        if (!res.success) {
          errors.push({ phone: phoneList[i], error: res.error });
        }
        setSendProgress((prev) => ({ ...prev, sent: i + 1, errors }));
      }

      if (errors.length === 0) {
        setResult({ success: true, message: `${phoneList.length} SMS enviados com sucesso!` });
        setBulkPhones('');
        setMessage('');
        setSelectedTemplate(null);
      } else {
        setResult({ success: false, error: `${errors.length} de ${phoneList.length} falharam` });
      }
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 7 }}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">
              <SendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('smsSendMessage')}
            </Typography>
            <ToggleButtonGroup
              value={bulkMode ? 'bulk' : 'single'}
              exclusive
              onChange={(_, v) => v && setBulkMode(v === 'bulk')}
              size="small"
            >
              <ToggleButton value="single">
                <SmsIcon sx={{ mr: 0.5 }} fontSize="small" />
                {t('smsSend')}
              </ToggleButton>
              <ToggleButton value="bulk">
                <SmsIcon sx={{ mr: 0.5 }} fontSize="small" />
                {t('smsBulkSend')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Destinatário(s) */}
          {bulkMode ? (
            <TextField
              fullWidth
              margin="normal"
              label={t('smsBulkRecipients')}
              placeholder="5511999999999&#10;5521888888888&#10;5531777777777"
              value={bulkPhones}
              onChange={(e) => setBulkPhones(e.target.value)}
              helperText={`${phoneList.length} ${t('smsRecipient')}(s) ${t('sharedDetected')}`}
              multiline
              rows={4}
            />
          ) : (
            <TextField
              fullWidth
              margin="normal"
              label={t('smsRecipient')}
              placeholder="5511999999999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              helperText={t('smsRecipientHelper')}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">+</InputAdornment>,
                },
              }}
            />
          )}

          {/* Seletor Template/Livre */}
          <Box mt={2} mb={1}>
            <ToggleButtonGroup
              value={useTemplate ? 'template' : 'free'}
              exclusive
              onChange={(_, v) => {
                if (v) {
                  setUseTemplate(v === 'template');
                  if (v === 'free') {
                    setSelectedTemplate(null);
                  }
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="free" sx={{ flex: 1 }}>
                {t('smsFreeMessage')}
              </ToggleButton>
              <ToggleButton value="template" sx={{ flex: 1 }}>
                {t('smsUseTemplate')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Templates */}
          {useTemplate && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('smsSelectTemplate')}:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {SMS_MESSAGE_TEMPLATES.map((tmpl) => (
                  <Chip
                    key={tmpl.name}
                    label={tmpl.name}
                    onClick={() => handleTemplateSelect(tmpl)}
                    color={selectedTemplate?.name === tmpl.name ? 'primary' : 'default'}
                    variant={selectedTemplate?.name === tmpl.name ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Mensagem */}
          <TextField
            fullWidth
            margin="normal"
            label={t('smsMessage')}
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            helperText={`${charCount}/160 ${t('smsCharacters')} (${smsCount} SMS)`}
            slotProps={{
              htmlInput: { maxLength: 320 },
            }}
          />

          {/* Progresso envio em massa */}
          {loading && bulkMode && sendProgress.total > 1 && (
            <Box mt={2}>
              <LinearProgress
                variant="determinate"
                value={(sendProgress.sent / sendProgress.total) * 100}
              />
              <Typography variant="caption" color="text.secondary">
                {sendProgress.sent} / {sendProgress.total}
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleSend}
              disabled={loading || phoneList.length === 0 || !message || !smsConfig.smsApiKey}
              fullWidth
            >
              {bulkMode && phoneList.length > 1
                ? `${t('smsSend')} (${phoneList.length})`
                : t('smsSend')}
            </Button>
          </Box>

          {result && (
            <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {result.success
                ? result.message || t('smsSentSuccess')
                : `${t('sharedError')}: ${result.error}`}
            </Alert>
          )}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('smsPreview')}
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              minHeight: 100,
              wordBreak: 'break-word',
              color: 'text.primary',
              border: 1,
              borderColor: 'divider',
            }}
          >
            {message || t('smsTypeMessage')}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('smsTo')}: {bulkMode ? `${phoneList.length} ${t('smsRecipient')}(s)` : phone || '---'}
          </Typography>

          {/* Templates de Mensagem */}
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              {t('smsTemplates')}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {SMS_MESSAGE_TEMPLATES.slice(0, 3).map((tmpl) => (
                <Box
                  key={tmpl.name}
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                  onClick={() => {
                    setMessage(tmpl.message);
                    setSelectedTemplate(tmpl);
                    setUseTemplate(true);
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    {tmpl.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {tmpl.message.substring(0, 50)}...
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ConfiguratorPage = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const admin = useAdministrator();
  const userId = useSelector((state) => state.session.user?.id);

  // Tab state
  const [activeTab, setActiveTab] = useState(TAB_IDS.COMMANDS);

  // SMS Config state
  const [smsConfig, setSmsConfig] = useState({
    smsApiKey: '',
    smsSenderId: '',
    serverDns: '',
    serverIp: '',
  });
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsTestResult, setSmsTestResult] = useState(null);
  const [smsBalance, setSmsBalance] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load SMS config from user attributes
  useEffectAsync(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`/api/users/${userId}`);
      if (response.ok) {
        const user = await response.json();
        const form = user.attributes?.form || {};
        setSmsConfig({
          smsApiKey: form.smsApiKey || '',
          smsSenderId: form.smsSenderId || '',
          serverDns: form.serverDns || '',
          serverIp: form.serverIp || '',
        });
      }
    } catch (e) {
      console.error('Error loading SMS config:', e);
    }
  }, [userId]);

  // Save SMS config
  const handleSaveSmsConfig = useCallback(async () => {
    setSmsLoading(true);
    try {
      const response = await fetch(`/api/users/${userId}`);
      if (!response.ok) throw new Error('Failed to load user');
      const user = await response.json();

      const updatedUser = {
        ...user,
        attributes: {
          ...user.attributes,
          form: {
            ...(user.attributes?.form || {}),
            ...smsConfig,
          },
        },
      };

      const saveResponse = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedUser),
      });

      if (!saveResponse.ok) throw new Error('Failed to save');

      setSnackbar({ open: true, message: t('sharedSaved'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSmsLoading(false);
    }
  }, [userId, smsConfig, t]);

  // Test SMS connection
  const handleTestConnection = useCallback(async () => {
    setSmsLoading(true);
    setSmsTestResult(null);
    try {
      const { smsApiKey } = smsConfig;
      const response = await fetch(`${SMS_API_URL}/credits`, {
        headers: { 'auth-key': smsApiKey },
      });

      if (response.ok) {
        const data = await response.json();
        const balance = data.Object ?? data.balance ?? data.credits ?? 0;
        setSmsTestResult({ success: true, balance });
        setSmsBalance(balance);
      } else {
        setSmsTestResult({ success: false, error: `HTTP ${response.status}` });
      }
    } catch (e) {
      setSmsTestResult({ success: false, error: e.message });
    } finally {
      setSmsLoading(false);
    }
  }, [smsConfig]);

  // Refresh balance
  const handleRefreshBalance = useCallback(async () => {
    setSmsLoading(true);
    try {
      const { smsApiKey } = smsConfig;
      const response = await fetch(`${SMS_API_URL}/credits`, {
        headers: { 'auth-key': smsApiKey },
      });

      if (response.ok) {
        const data = await response.json();
        setSmsBalance(data.Object ?? data.balance ?? data.credits ?? 0);
      }
    } catch (e) {
      console.error('Error refreshing balance:', e);
    } finally {
      setSmsLoading(false);
    }
  }, [smsConfig]);

  // Send SMS
  const handleSendSms = useCallback(async (phone, message) => {
    setSmsLoading(true);
    try {
      const { smsApiKey, smsSenderId } = smsConfig;
      const response = await fetch(`${SMS_API_URL}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-key': smsApiKey,
        },
        body: JSON.stringify({
          Sender: smsSenderId || 'GPS',
          Receivers: phone,
          Content: message,
        }),
      });

      const data = await response.json();
      const success = data.Success === true;

      return { success, error: data.Message };
    } catch (e) {
      return { success: false, error: e.message };
    } finally {
      setSmsLoading(false);
    }
  }, [smsConfig]);

  if (!admin) {
    return (
      <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'configuratorTitle']}>
        <Container maxWidth={false} className={classes.container}>
          <Typography color="error">{t('errorGeneral')}</Typography>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'configuratorTitle']}>
      <Container maxWidth={false} className={classes.container}>
        <Box className={classes.fullHeight}>
          {/* Tabs Header */}
          <Paper className={classes.tabsContainer}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="configurator tabs"
            >
              <Tab icon={<SettingsRemoteIcon />} label={t('commandTitle')} iconPosition="start" />
              <Tab icon={<SimCardIcon />} label={t('configApn')} iconPosition="start" />
              <Tab icon={<DevicesIcon />} label={t('configDevices')} iconPosition="start" />
              <Tab icon={<HistoryIcon />} label={t('configHistory')} iconPosition="start" />
              <Tab icon={<SettingsIcon />} label={t('smsConfiguration')} iconPosition="start" />
              <Tab icon={<SendIcon />} label={t('smsSend')} iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          <Box className={classes.tabContent}>
            <TabPanel value={activeTab} index={TAB_IDS.COMMANDS}>
              <CommandsTabV2 classes={classes} t={t} smsConfig={smsConfig} />
            </TabPanel>

            <TabPanel value={activeTab} index={TAB_IDS.APN_CONFIG}>
              <ApnConfigTab t={t} />
            </TabPanel>

            <TabPanel value={activeTab} index={TAB_IDS.DEVICE_CONFIG}>
              <DeviceConfigTab t={t} />
            </TabPanel>

            <TabPanel value={activeTab} index={TAB_IDS.COMMAND_HISTORY}>
              <CommandHistoryTab t={t} />
            </TabPanel>

            <TabPanel value={activeTab} index={TAB_IDS.SMS_CONFIG}>
              <SmsConfigTab
                classes={classes}
                t={t}
                smsConfig={smsConfig}
                setSmsConfig={setSmsConfig}
                onSave={handleSaveSmsConfig}
                onTest={handleTestConnection}
                loading={smsLoading}
                testResult={smsTestResult}
                balance={smsBalance}
                onRefreshBalance={handleRefreshBalance}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={TAB_IDS.SMS_SEND}>
              <SmsSendTab
                t={t}
                smsConfig={smsConfig}
                onSend={handleSendSms}
                loading={smsLoading}
              />
            </TabPanel>
          </Box>
        </Box>

        {/* Global Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PageLayout>
  );
};

export default ConfiguratorPage;
